/**
 * Dispose Utilities
 * 
 * Helper functions for properly disposing Three.js resources
 * to prevent memory leaks.
 */

import * as THREE from 'three';

/**
 * Dispose a material and its textures
 */
export function disposeMaterial(material: THREE.Material): void {
  // Dispose textures
  for (const key of Object.keys(material)) {
    const value = (material as unknown as Record<string, unknown>)[key];
    if (value instanceof THREE.Texture) {
      value.dispose();
    }
  }
  
  material.dispose();
}

/**
 * Dispose a mesh's geometry and materials
 */
export function disposeMesh(mesh: THREE.Mesh): void {
  mesh.geometry?.dispose();
  
  if (Array.isArray(mesh.material)) {
    mesh.material.forEach(disposeMaterial);
  } else if (mesh.material) {
    disposeMaterial(mesh.material);
  }
}

/**
 * Recursively dispose all objects in a scene or group
 */
export function disposeObject(object: THREE.Object3D): void {
  // Dispose children first
  while (object.children.length > 0) {
    disposeObject(object.children[0]);
    object.remove(object.children[0]);
  }
  
  // Dispose mesh resources
  if (object instanceof THREE.Mesh) {
    disposeMesh(object);
  }
  
  // Dispose other resource types
  if (object instanceof THREE.Line) {
    object.geometry?.dispose();
    if (Array.isArray(object.material)) {
      object.material.forEach((m) => m.dispose());
    } else {
      object.material?.dispose();
    }
  }
  
  if (object instanceof THREE.Points) {
    object.geometry?.dispose();
    if (Array.isArray(object.material)) {
      object.material.forEach((m) => m.dispose());
    } else {
      object.material?.dispose();
    }
  }
}

/**
 * Dispose an entire scene
 */
export function disposeScene(scene: THREE.Scene): void {
  // Dispose environment and background
  if (scene.environment) {
    scene.environment.dispose();
  }
  if (scene.background instanceof THREE.Texture) {
    scene.background.dispose();
  }
  
  // Dispose all objects
  disposeObject(scene);
}

/**
 * Track and dispose resources
 */
export class ResourceTracker {
  private resources = new Set<THREE.Object3D | THREE.Material | THREE.Texture | THREE.BufferGeometry>();
  
  /**
   * Track a resource for later disposal
   */
  public track<T extends THREE.Object3D | THREE.Material | THREE.Texture | THREE.BufferGeometry>(
    resource: T
  ): T {
    this.resources.add(resource);
    return resource;
  }
  
  /**
   * Track all resources in a scene/group hierarchy
   */
  public trackAll(object: THREE.Object3D): void {
    object.traverse((child) => {
      this.resources.add(child);
      
      if (child instanceof THREE.Mesh) {
        if (child.geometry) {
          this.resources.add(child.geometry);
        }
        if (Array.isArray(child.material)) {
          child.material.forEach((m) => {
            this.resources.add(m);
            this.trackMaterialTextures(m);
          });
        } else if (child.material) {
          this.resources.add(child.material);
          this.trackMaterialTextures(child.material);
        }
      }
    });
  }
  
  /**
   * Track textures in a material
   */
  private trackMaterialTextures(material: THREE.Material): void {
    for (const key of Object.keys(material)) {
      const value = (material as unknown as Record<string, unknown>)[key];
      if (value instanceof THREE.Texture) {
        this.resources.add(value);
      }
    }
  }
  
  /**
   * Untrack a resource
   */
  public untrack(resource: THREE.Object3D | THREE.Material | THREE.Texture | THREE.BufferGeometry): void {
    this.resources.delete(resource);
  }
  
  /**
   * Dispose all tracked resources
   */
  public dispose(): void {
    for (const resource of this.resources) {
      if (resource instanceof THREE.Object3D) {
        if (resource.parent) {
          resource.parent.remove(resource);
        }
      }
      
      if ('dispose' in resource && typeof resource.dispose === 'function') {
        resource.dispose();
      }
    }
    
    this.resources.clear();
  }
}
